/**
 * @fileoverview This file contains the ServiceWorkerManagmentService, responsible for
 * managing Service Worker operations and communications.
 */

import {delay, filter, forkJoin, map, of, switchMap, tap} from 'rxjs';
import {Workbox} from 'workbox-window';

import {inject, Injectable, signal} from '@angular/core';
import {StorageKeys} from '@core/storage';
import {setGlobalWorkbox} from '@shared-types';

import {IServiceWorkerCacheAnalysis, IServiceWorkerConfig, IServiceWorkerLogEntry, IServiceWorkerVersion} from '../../data';
import {ServiceWorkerChannelService} from './service-worker-channel.service';

@Injectable({
	providedIn: 'root',
})
export class ServiceWorkerManagmentService {
	/* ------------------------------------ Services and Providers ------------------------------------ */
	private wb: Workbox | null = null;
	public readonly swChannelService = inject(ServiceWorkerChannelService);

	/* ---------------------------------------- Initialization --------------------------------------- */
	/**
	 * Initializes the service with a Workbox instance.
	 * @param workbox - The Workbox instance to use.
	 * @throws Error if Workbox instance is not set.
	 */
	public initialize(workbox: Workbox): void {
		this.wb = workbox;
		if (!this.wb) throw new Error('[ServiceWorkerManagementService] Workbox instance is not set');
		this.swChannelService.initialize(workbox);
		this._setupEventListeners();
	}

	/**
	 * Loads all data related to the Service Worker.
	 * @returns An observable that emits an object containing logs, version, config, and cache analysis.
	 */
	public loadAllData() {
		return this.SwInfo.checkSwConnection().pipe(
			filter(connected => connected),
			switchMap(() => {
				return forkJoin({
					logs: this.Logging.getLogs(),
					version: this.Versioning.getSwVersion(),
					config: this.SwConfigutarions.getSwConfig(),
					cacheAnalysis: this.Caching.getCacheAnalysis$(true),
				});
			}),
		);
	}

	/* ----------------------------------- Service Worker Info -------------------------------------- */
	public readonly SwInfo = {
		swConnected$: signal<boolean>(null),

		checkSwConnection: () => {
			if (this.wb === null) return of(false);
			return this.swChannelService
				.newSendMessageToSw({
					action: 'ping',
					params: [],
				})
				.pipe(
					map(response => response === 'pong'),
					tap(connected => this.SwInfo.swConnected$.set(connected)),
				);
		},

		registerSw: async () => {
			let registration: ServiceWorkerRegistration;
			if ('serviceWorker' in navigator) {
				const SW_PATH = '/sw.js';
				const wb = new Workbox(SW_PATH);
				setGlobalWorkbox(wb);
				registration = await wb.register();
			}
			if (registration) this.SwInfo.swConnected$.set(true);
			return registration;
		},

		unregisterSw: async () => {
			if ('serviceWorker' in navigator) {
				const registration = await navigator.serviceWorker.getRegistration();
				if (registration) await registration.unregister();
				this.SwInfo.swConnected$.set(false);
				setGlobalWorkbox(null);
			}
		},
	};

	/* ------------------------------------------ Logging ------------------------------------------- */
	public readonly Logging = {
		logs$: signal<IServiceWorkerLogEntry[]>(null),

		getLogs: (force = false) => {
			if (!force && this.Logging.logs$() !== null) return of(this.Logging.logs$());
			return this.swChannelService.newSendMessageToSw({action: 'getLogs', params: []}).pipe(tap(logs => this.Logging.logs$.set(logs)));
		},
	};

	/* ------------------------------------------ Caching ------------------------------------------- */
	public readonly Caching = {
		analysis$: signal<IServiceWorkerCacheAnalysis>(null),

		getCacheAnalysis$: (force = false) => {
			if (!force && this.Caching.analysis$() !== null) return of(this.Caching.analysis$());
			return this.swChannelService.newSendMessageToSw({action: 'getCacheAnalysis', params: []}).pipe(tap(analysis => this.Caching.analysis$.set(analysis)));
		},

		clearCacheDeltas: () => this.swChannelService.newSendMessageToSw({action: 'clearCacheDeltas', params: []}),
	};

	/* ---------------------------------------- Versioning ------------------------------------------ */
	public readonly Versioning = {
		swVersion$: signal<IServiceWorkerVersion>({version: 'N/A', releaseDate: null}),
		isCheckingUpdates$: signal<boolean>(false),

		getSwVersion: () => this.swChannelService.newSendMessageToSw({action: 'getVersion', params: []}).pipe(tap(version => this.Versioning.swVersion$.set(version))),

		checkUpdates: () => {
			this.Versioning.isCheckingUpdates$.set(true);
			return this.swChannelService.newSendMessageToSw({action: 'checkForUpdates', params: []}).pipe(
				delay(1000),
				tap(hasUpdates => {
					if (hasUpdates) this.Versioning.swVersion$.set({version: 'N/A', releaseDate: null});
					this.Versioning.isCheckingUpdates$.set(false);
				}),
			);
		},

		clearAndReinstallApp: () => this.swChannelService.newSendMessageToSw({action: 'clearSourceCaches', params: [true]}),
	};

	/* ------------------------------------- Configurations ----------------------------------------- */
	public readonly SwConfigutarions = {
		swConfig$: signal<IServiceWorkerConfig>(null),
		getSwConfig: () => this.swChannelService.newSendMessageToSw({action: 'getConfig', params: []}).pipe(tap(config => this.SwConfigutarions.swConfig$.set(config))),
	};

	/* -------------------------------------- Event Listeners --------------------------------------- */
	/**
	 * Sets up event listeners for messages from the Service Worker.
	 */
	private _setupEventListeners(): void {
		this.swChannelService.newSendMessageToSw({action: 'clientReloaded', params: []}).subscribe();
		this.swChannelService.onEvent('reloadPage').subscribe(value => {
			if (value?.payload) localStorage.setItem(StorageKeys.GlobalKeys.SW_VERSION, value.payload.version);
			this.reloadPage();
		});
	}

	/**
	 * Reloads the page to apply a new version of the Service Worker.
	 */
	public reloadPage() {
		console.log('Reloading the page to apply the new version.');
		window.location.reload();
	}
}
